import React, { useMemo, useState } from "react";
import { Alert, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useUserInfoQuery } from "../../../api/userApiSlice";
import { useGetDUCInfoQuery } from "../../../api/ducApiSlice";
import { getRenewalStatus } from "../utils/renewalWindow";
import { colours } from "../../../StyleConstants";

// Returns the most recent Approved DUC in a list (iterate from the end), or null.
const getLatestApproved = (list?: any[]): any | null => {
    if (!list || list.length === 0) return null;
    for (let i = list.length - 1; i >= 0; i--) {
        if (list[i]?.status === "Approved") return list[i];
    }
    return null;
};

const DucExpiryBanner: React.FC = () => {
    const { data: userData } = useUserInfoQuery({});
    const { data: ducDataObject } = useGetDUCInfoQuery({ email: userData?.email });

    // Evaluate the renewal window for the latest Approved GENERAL and AIAN DUCs.
    // "About to expire" = approved, not yet expired, within the 3-month window
    // (single source of truth: getRenewalStatus).
    const { expiring } = useMemo(() => {
        const general = getLatestApproved(ducDataObject?.GENERAL as any[] | undefined);
        const aian = getLatestApproved(ducDataObject?.AIAN as any[] | undefined);

        const generalStatus = getRenewalStatus({
            status: general?.status,
            expiresOn: general?.expiresOn,
        });
        const aianStatus = getRenewalStatus({
            status: aian?.status,
            expiresOn: aian?.expiresOn,
        });

        return {
            expiring: generalStatus.expiring || aianStatus.expiring,
        };
    }, [ducDataObject]);

    // Dismissal is in-memory only, so it resets to false every time the page is
    // accessed (the component remounts). The banner reappears on each visit while
    // the certification is still within the renewal window.
    const [dismissed, setDismissed] = useState(false);

    if (!expiring || dismissed) return null;

    const handleClose = () => {
        setDismissed(true);
    };

    return (
        <Alert
            role="alert"
            onClose={handleClose}
            icon={<WarningAmberRoundedIcon fontSize="inherit" sx={{ color: colours.core.white }} />}
            sx={{
                backgroundColor: colours.warning.warn,
                color: colours.core.white,
                borderRadius: 1,
                alignItems: "center",
                "& .MuiAlert-action": { color: colours.core.white },
                "& .MuiAlert-action .MuiSvgIcon-root": { color: colours.core.white },
            }}
        >
            <Typography fontWeight="500" sx={{ color: colours.core.white }}>
                Your Data Access Certification is about to expire. Renew your application to maintain access.
            </Typography>
        </Alert>
    );
};

export default DucExpiryBanner;
